import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTourStore } from '@/stores/tour';
import { Button } from '@/components/ui/Button';

interface TourStep {
  targetSelector: string;
  title: string;
  body: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const ADMIN_STEPS: TourStep[] = [
  {
    targetSelector: '[data-tour="sidebar-organizations"]',
    title: 'Organizations',
    body: "Manage all client organizations here. Each org gets an isolated workspace with its own folders, files, and users. Use 'Onboard new client' for the guided setup.",
    position: 'right',
  },
  {
    targetSelector: '[data-tour="sidebar-settings"]',
    title: 'Settings',
    body: 'Everything in one place — staff users, folder templates, request templates, audit log, and system configuration are all accessible from the Settings tabs.',
    position: 'right',
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getTooltipStyle(
  rect: Rect,
  position: TourStep['position'],
): React.CSSProperties {
  const gap = 12;
  const tooltipWidth = 320;

  switch (position) {
    case 'right':
      return {
        top: rect.top,
        left: rect.left + rect.width + gap,
      };
    case 'left':
      return {
        top: rect.top,
        left: rect.left - tooltipWidth - gap,
      };
    case 'bottom':
      return {
        top: rect.top + rect.height + gap,
        left: rect.left,
      };
    case 'top':
      return {
        top: rect.top - gap,
        left: rect.left,
        transform: 'translateY(-100%)',
      };
  }
}

export function AdminGuidedTour() {
  const showAdminTour = useTourStore((s) => s.showAdminTour);
  const adminTourStep = useTourStore((s) => s.adminTourStep);
  const nextAdminStep = useTourStore((s) => s.nextAdminStep);
  const dismissAdminTour = useTourStore((s) => s.dismissAdminTour);

  const [targetRect, setTargetRect] = useState<Rect | null>(null);

  // Find visible steps (skip any whose target doesn't exist in the DOM)
  const visibleSteps = useMemo(() => {
    if (!showAdminTour) return [];
    return ADMIN_STEPS.filter(
      (step) => document.querySelector(step.targetSelector) !== null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdminTour, adminTourStep]);

  const currentVisible = visibleSteps[adminTourStep] ?? null;
  const totalVisible = visibleSteps.length;

  // If step exceeds available steps, auto-dismiss
  useEffect(() => {
    if (showAdminTour && adminTourStep >= totalVisible && totalVisible > 0) {
      dismissAdminTour();
    }
  }, [showAdminTour, adminTourStep, totalVisible, dismissAdminTour]);

  // Measure the target element
  const measureTarget = useCallback(() => {
    if (!currentVisible) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(currentVisible.targetSelector);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setTargetRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [currentVisible]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget]);

  if (!showAdminTour || !currentVisible || !targetRect || totalVisible === 0) {
    return null;
  }

  const isLast = adminTourStep === totalVisible - 1;
  const pad = 6;

  const handleNext = () => {
    if (isLast) {
      dismissAdminTour();
    } else {
      nextAdminStep();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999]" aria-modal="true" role="dialog">
      {/* Dark backdrop with a hole cut out around the target */}
      <div
        className="absolute inset-0"
        style={{
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
          top: targetRect.top - pad,
          left: targetRect.left - pad,
          width: targetRect.width + pad * 2,
          height: targetRect.height + pad * 2,
          position: 'fixed',
          pointerEvents: 'none',
        }}
      />

      {/* Clickable backdrop area to dismiss */}
      <div
        className="absolute inset-0"
        onClick={dismissAdminTour}
        style={{ background: 'transparent' }}
      />

      {/* Tooltip card */}
      <div
        className="fixed z-[10000] w-[320px] bg-white border border-slate-200 shadow-lg p-4"
        style={getTooltipStyle(targetRect, currentVisible.position)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold text-bb-navy mb-1">
          {currentVisible.title}
        </div>
        <p className="text-sm text-slate-600 mb-4">{currentVisible.body}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">
            {adminTourStep + 1} / {totalVisible}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={dismissAdminTour}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Skip tour
            </button>
            <Button variant="primary" onClick={handleNext} className="text-xs">
              {isLast ? 'Done!' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
